import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TimelineSharpIcon from "@mui/icons-material/TimelineSharp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupIcon from "@mui/icons-material/Group";
import HomeIcon from "@mui/icons-material/Home";

import NBA_LOGO from "../../images/nba_logo.png";
import RouteConstants from "../../common/RouteConstants";
import { RoutePermissions, RouteScreens } from "../../common/RoutePermissions";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";

const useStyles = makeStyles({
  drawerPaper: {
    width: 300,
    backgroundImage: "linear-gradient(90deg, #0f244a, #24428a)",
    color: "#ffff",
    boxShadow: "8px 0 30px rgba(0,0,0,0.4)",
  },
  header: {
    padding: 24,
    position: "relative",
    flexShrink: 0,
  },
  logoTextContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerCaption: {
    fontWeight: 700,
    letterSpacing: 1,
  },
  userName: {
    opacity: 0.7,
    marginTop: 4,
  },
  closeButton: {
    position: "absolute",
    right: 14,
    top: 28,
    color: "#ffffff",
    background: "#ffffff",
    "&:hover": {
      background: "#ffffff",
    },
  },
  menuContainer: {
    flex: 1,
    overflowY: "auto",
  },
  menuItemButton: {
    position: "relative",
    margin: "4px 16px",
    padding: "8px 24px",
    borderRadius: 14,
    overflow: "hidden",
    transition: "all 0.3s ease",
    color: "#fff",

    "&:hover": {
      background: "linear-gradient(135deg, #2e5cb8 0%, #3f78bf 100%)",
    },
  },
  menuItemButtonActive: {
    background: "linear-gradient(to bottom right, #eff6ff, #ffffff)",
    "&:hover": {
      background: "linear-gradient(to bottom right, #eff6ff, #ffffff)",
    },
  },
  activeGlow: {
    position: "absolute",
    left: 0,
    top: 6,
    bottom: 6,
    width: 10,
    borderRadius: "0 6px 6px 0",
    backgroundColor: "#3f78bf",
    boxShadow: "0 0 10px rgb(57 85 160 / 60%)",
  },

  listItemIcon: {
    minWidth: 38,
  },
  logoutBox: {
    padding: 24,
    flexShrink: 0,
  },
  logoutButton: {
    borderRadius: 14,
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #2e5cb8 0%, #3f78bf 100%)",
    },
  },
  logoutIcon: {
    color: "#fff",
  },
  logoutText: {
    fontWeight: 600,
    fontSize: "1rem",
    color: "#fff",
  },
});

const RenderItem = ({
  label,
  icon,
  onClick,
  active,
  setActive,
  onClose,
  classes,
}) => {
  const isActive = active === label;

  const handleClick = () => {
    setActive(label);
    if (onClick) onClick();
    if (onClose) onClose();
  };

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={handleClick}
        className={`${classes.menuItemButton} ${
          isActive ? classes.menuItemButtonActive : ""
        }`}
      >
        {isActive && <Box className={classes.activeGlow} />}
        <ListItemIcon
          className={classes.listItemIcon}
          style={{
            color: isActive ? "#1d428a" : "#ffffff",
            transform: isActive ? "scale(1.15)" : "scale(1)",
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontWeight: isActive ? 600 : 400,
            letterSpacing: isActive ? 0.5 : 0,
            fontSize: "0.9rem",
            color: isActive ? "#1d428a" : "#ffffff",
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const AppSidebar = ({ open, onClose, user, eventId, userAppRoles, logout }) => {
  const classes = useStyles();
  const [active, setActive] = useState("Home");
  const navigate = useNavigate();

  const navigateToHomeScreen = () => {
    navigate(RouteConstants.HOME);
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box className={classes.header}>
          <TitleTooltip title="Close" placement="right">
            <IconButton onClick={onClose} className={classes.closeButton}>
              <ArrowBackIosNewIcon sx={{ color: "#ff0000", fontSize: 18 }} />
            </IconButton>
          </TitleTooltip>

          <Box className={classes.logoTextContainer}>
            <img
              src={NBA_LOGO}
              alt="Logo"
              className={`${classes.logo} appbar-nba-logo appbar-nba-logo-rightMargin`}
            />
            <Typography
              variant="h6"
              className={`${classes.headerCaption} headerTitle`}
            >
              Combine Central
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.15)" }} />

        <Box className={classes.menuContainer}>
          <List sx={{ mt: 2 }}>
            <RenderItem
              label="Home"
              icon={<HomeIcon />}
              onClick={navigateToHomeScreen}
              active={active}
              setActive={setActive}
              classes={classes}
              onClose={onClose}
            />
            <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", my: 1 }} />

            {RoutePermissions.IsAuthorized(
              RouteScreens.TeamBallotDashboard,
              userAppRoles
            ) && (
              <RenderItem
                label="Combine Team Ballot"
                icon={<DashboardIcon />}
                onClick={() => navigate(RouteConstants.TEAM_BALLOT_DASHBOARD)}
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {RoutePermissions.IsAuthorized(
              RouteScreens.TeamGreenBallotVote,
              userAppRoles
            ) && (
              <RenderItem
                label="Green Room Ballot"
                icon={<DashboardIcon />}
                onClick={() => navigate(RouteConstants.TEAM_GREEN_BALLOT_VOTE)}
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {RoutePermissions.IsAuthorized(
              RouteScreens.TeamGLeagueBallot,
              userAppRoles
            ) && (
              <RenderItem
                label="GLeague Ballot"
                icon={<DashboardIcon />}
                onClick={() =>
                  navigate(RouteConstants.TEAM_TOP_FIVE_BALLOT_VOTE)
                }
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {RoutePermissions.IsAuthorized(
              RouteScreens.AdminBallotDashboard,
              userAppRoles
            ) && (
              <RenderItem
                label="Admin Ballot Dashboard"
                icon={<DashboardIcon />}
                onClick={() => navigate(RouteConstants.ADMIN_BALLOT_DASHBOARD)}
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {RoutePermissions.IsAuthorized(
              RouteScreens.AdminPreferences,
              userAppRoles
            ) && (
              <RenderItem
                label="Admin Preferences"
                icon={<AdminPanelSettingsIcon />}
                onClick={() => navigate(RouteConstants.ADMIN_PREFERENCES)}
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {(user.isAdmin ||
              (RoutePermissions.IsAuthorized(
                RouteScreens.ViewPlayerProfile,
                userAppRoles
              ) &&
                user.playerProfileFlag)) && (
              <RenderItem
                label="Player Profile"
                icon={<PersonOutlineIcon />}
                onClick={() =>
                  navigate(`/${eventId}${RouteConstants.PLAYER_LIST}`)
                }
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {(user.isAdmin ||
              (RoutePermissions.IsAuthorized(
                RouteScreens.CombineResults,
                userAppRoles
              ) &&
                user.combineResultsFlag)) && (
              <RenderItem
                label="Combine Results"
                icon={<DashboardIcon />}
                onClick={() =>
                  navigate(`${eventId}${RouteConstants.COMBINE_RESULTS}`)
                }
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {RoutePermissions.IsAuthorized(
              RouteScreens.BoxScore,
              userAppRoles
            ) && (
              <RenderItem
                label="Box Score"
                icon={<AdminPanelSettingsIcon />}
                onClick={() => navigate(RouteConstants.BOX_SCORE)}
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {(user.isAdmin ||
              RoutePermissions.IsAuthorized(
                RouteScreens.CareerStatsDashboard,
                userAppRoles
              )) && (
              <RenderItem
                label="Comparables"
                icon={<TimelineSharpIcon />}
                onClick={() =>
                  navigate(`/${eventId}${RouteConstants.CAREER_STATS}`)
                }
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {user.isCompleteTeamUser && user.isDisplayTeamRosterFlag && (
              <RenderItem
                label="Team Rosters"
                icon={<GroupIcon />}
                onClick={() => navigate(RouteConstants.VIEW_TEAM_ROSTER)}
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}

            {RoutePermissions.IsAuthorized(
              RouteScreens.EventCalendar,
              userAppRoles
            ) && (
              <RenderItem
                label="Event Calendar"
                icon={<CalendarMonthIcon />}
                onClick={() => navigate(RouteConstants.EVENT_SCHEDULER)}
                active={active}
                setActive={setActive}
                classes={classes}
                onClose={onClose}
              />
            )}
          </List>
        </Box>

        <Box className={classes.logoutBox}>
          <ListItemButton onClick={logout} className={classes.logoutButton}>
            <ListItemIcon className={classes.logoutIcon} onClick={logout}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText
              primary="Sign Out"
              primaryTypographyProps={{ color: "#ff6b6b", fontWeight: 500 }}
              classes={{ primary: classes.logoutText }}
              onClick={logout}
            />
          </ListItemButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AppSidebar;
